import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, finalize } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { Account, CreateUserRequest } from '@app/_models';

import { Insights } from '../_services/insights.service';


const baseUrl = `${environment.apiUrl}/accounts`;

@Injectable({ providedIn: 'root' })
export class AccountService {
    private accountSubject: BehaviorSubject<Account>;
    public account: Observable<Account>;

    constructor(
        private router: Router,
        private http: HttpClient,
        private insights: Insights
    ) {
        this.accountSubject = new BehaviorSubject<Account>(null);
        this.account = this.accountSubject.asObservable();
    }

    public get accountValue(): Account {
        var account = this.accountSubject.value;

        if (account == null){
            account = this.getAccount();

            if(account.jwtToken == undefined){
                account = null
            }
        }

        return account;
    }

    login(email: string, password: string) {
        return this.http.post<any>(`${baseUrl}/authenticate`, { email, password }, { withCredentials: true })
            .pipe(map(account => {

                this.insights.setUserId(email);
                this.accountSubject.next(account);
                this.startRefreshTokenTimer();
                this.saveAccount(account);
                return account;
            }));
    }
    
    saveAccount(account: Account): void {
        const stored = this.getAccount();
    
        Object.assign(stored, account);
    
        sessionStorage.setItem('account', JSON.stringify(stored));
      }

      getAccount(): Account {
        const account = new Account();
    
        const stored = JSON.parse(sessionStorage.getItem('account'));
    
        Object.assign(account, stored);
    
        return account;
      }

      getResetPasswordRequestCount() : number {
        const count = parseInt(JSON.parse(window.sessionStorage.getItem('count')));
        return isNaN(count) ? 0 : count;
      }

     setRequestCount() : void {
        const stored = (this.getResetPasswordRequestCount() + 1).toString()
        window.sessionStorage.setItem('count', stored );
     }
    

    logout() {
        this.http.post<any>(`${baseUrl}/revoke-token`, {}, { withCredentials: true }).subscribe();
        this.stopRefreshTokenTimer();
        this.accountSubject.next(null);
        this.insights.clearUserId();
        sessionStorage.clear();
        this.router.navigate(['/account/login']);
    }

    refreshToken() {
        return this.http.post<any>(`${baseUrl}/refresh-token`, {}, { withCredentials: true })
            .pipe(map((account) => {
                this.accountSubject.next(account);
                //if(account == null)
                //{
                //    account = this.getAccount();
                //}
                //document.cookie = `refreshToken=${account.refreshToken}`;
                this.startRefreshTokenTimer();
                return account;
            }));
    }

    register(account: Account) {
        return this.http.post(`${baseUrl}/register`, account);
    }

    verifyEmail(token: string) {
        return this.http.post(`${baseUrl}/verify-email`, { token });
    }

    forgotPassword(email: string, forgotPasswordRequestCount: number) {
        return this.http.post(`${baseUrl}/forgot-password`, { email, forgotPasswordRequestCount });
    }

    validateResetToken(token: string) {
        return this.http.post(`${baseUrl}/validate-reset-token`, { token });
    }

    resetPassword(token: string, password: string, confirmPassword: string) {
        return this.http.post(`${baseUrl}/reset-password`, { token, password, confirmPassword });
    }

    newUserResetPassword(token: string, password: string, confirmPassword: string) {
        return this.http.post(`${baseUrl}/new-user-reset-password`, { token, password, confirmPassword });
    }

    getAll() {
        return this.http.get<Account[]>(baseUrl);
    }

    getById(id: string) {
        return this.http.get<Account>(`${baseUrl}/${id}`);
    }

    create(params) {
        return this.http.post(baseUrl, params);
    }

    userCreate(    FirstName: string,
        LastName: string,
        Email: string,
        PhoneNumber: string,
        Role: string,
        ClientID: number,
        IsActive: boolean) {
        return this.http.post<any>(`${baseUrl}/admin-create`, { FirstName, LastName, Email, PhoneNumber, Role, ClientID, IsActive });
    }

    getUsersByClientID(clientID: number){
        return this.http.get<Account[]>(`${baseUrl}/get-users?clientID=${clientID}`);
    }

    update(id, params) {
        return this.http.put(`${baseUrl}/${id}`, params)
            .pipe(map((account: any) => {
                // update the current account if it was updated
                if (account.id === this.accountValue.id) {
                    // publish updated account to subscribers
                    account = { ...this.accountValue, ...account };
                    this.accountSubject.next(account);
                }
                return account;
            }));
    }

    delete(id: string) {
        return this.http.delete(`${baseUrl}/${id}`)
            .pipe(finalize(() => {
                // auto logout if the logged in account was deleted
                if (id === this.accountValue.id)
                    this.logout();
            }));
    }

    changePassword(params: any) {
        return this.http.post(`${baseUrl}/change-password`, params);
    }

    returnUserContact(params: any) {
        return this.http.get(`${baseUrl}/parse-user-token/?token=${params}`).pipe();
    }

    // helper methods

    private refreshTokenTimeout;

    private startRefreshTokenTimer() {
      if (this.accountValue?.jwtToken != null)
      {
        // parse json object from base64 encoded jwt token
        const jwtToken = JSON.parse(atob(this.accountValue.jwtToken.split('.')[1]));

        // set a timeout to refresh the token a minute before it expires
        const expires = new Date(jwtToken.exp * 1000);
        const timeout = expires.getTime() - Date.now() - (60 * 1000);
        this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
      }
    }

    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }


}
